import { Directive, computed, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { take } from 'rxjs/operators';

import { Widgets } from '@common/enums';
import { ESelectedEntityType } from '@common/types/layout.type';
import type { WidgetParams } from '@common/types/content.type';
import type { WidgetErrorItem } from '@common/types/widget-errors.type';

import type { DenormalizedColumnEntity, WebLegacyWidgetEntity } from '@web/types';

import { WebWidgetsService } from '@web/core/services/web-widgets.service';
import { BlocksFacade } from '@web/store/page-structure/blocks/blocks.facade';
import { LayoutFacade } from '@web/store/layout/layout.facade';
import { NavigationDrawerService } from '@web/layout/drawers/navigation-drawer/navigation-drawer.service';

/**
 * Editor chrome shared by widget hosts that drive a **signal form** — the
 * signal-side twin of `BaseWidgetComponent`.
 *
 * Deliberately much smaller than that one. A signal host copies and removes
 * through its own form (`form.copy()` / `form.remove()`) rather than the widgets
 * facade, and it never opens the add-widget modal — adding is the column's job,
 * not the widget's. So the whole modal/facade half of the FormGroup base
 * (`modalService`, `tabsFacade`, `gridsFacade`, `widgetsFacade`, `columnsFacade`,
 * `productRestrictionFacade`, `appFacade`, `isSpecific`, `addWidget`,
 * `animationHandler`, `isCrmProductWidgetRestricted$`) is not carried over, and
 * `copyWidget`/`removeWidget` are abstract instead of facade-backed.
 */
@Directive({})
export abstract class BaseSignalWidgetComponent {
    public readonly widget = input.required<WebLegacyWidgetEntity>();
    public readonly widgetErrors = input<WidgetErrorItem>();
    public readonly index = input.required<number>();
    public readonly column = input<DenormalizedColumnEntity>();
    public readonly blockId = input<string>();
    public readonly gridId = input<string>();
    public readonly isTablet = input<boolean>(false);
    public readonly isMobile = input<boolean>(false);

    protected readonly layoutFacade: LayoutFacade = inject(LayoutFacade);
    protected readonly blocksFacade: BlocksFacade = inject(BlocksFacade);
    protected readonly widgetsService: WebWidgetsService = inject(WebWidgetsService);
    protected readonly navDrawerService: NavigationDrawerService = inject(NavigationDrawerService);

    private readonly selectedEntityId = toSignal(this.layoutFacade.entityId$, { initialValue: '' });

    public readonly hideControls = toSignal(this.layoutFacade.hideControls$, { initialValue: false });

    public readonly isWidgetSelected = computed<boolean>(() => this.selectedEntityId() === this.widget().id);

    /** Editor capabilities of this widget type — has a settings panel, shows controls, … */
    public readonly extendedWidgetParams = computed<WidgetParams | null>(() =>
        this.widgetsService.getExtendedWidgetParams(this.widget().name)
    );

    public readonly isShowMoreActionState = signal<boolean>(false);

    /** Both go through the widget's own signal form, so each host binds them itself. */
    public abstract copyWidget(): void;
    public abstract removeWidget(): void;

    public selectWidget(): void {
        const widget = this.widget();

        // The cart icon has no settings of its own — it is edited as a step of
        // the header block that hosts it.
        if (widget.name === Widgets.SHOP_CART_ICON) {
            this.blocksFacade.blocks$.pipe(take(1)).subscribe((blocks) =>
                this.layoutFacade.setSelectedEntity({
                    entityType: ESelectedEntityType.specificBlock,
                    entityId: blocks[0].id,
                    entityStep: 'cart_icon',
                })
            );
            return;
        }

        this.layoutFacade.setSelectedEntity({ entityType: ESelectedEntityType.widget, entityId: widget.id });

        this.navDrawerService.selectInNavigation({
            entityId: widget.id,
            blockId: this.blockId(),
            sectionId: this.column()?.parentId,
            columnId: this.column()?.id,
            gridId: this.gridId(),
        });
    }

    public setMoreActionState(state: boolean): void {
        this.isShowMoreActionState.set(state);
    }

    public dragStarted(): void {
        this.layoutFacade.setIsEntityDragging(true);
    }

    public dragEnded(): void {
        this.layoutFacade.setIsEntityDragging(false);
    }
}
